import { useCustom, useTranslate } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { Alert, Button, Modal, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { photoFailureOf } from './model';

/**
 * E34 · the paper-photo viewer (the founder's Q-b1b-2 A, 2026-09-28; cc-homework, "Client Hub —
 * Homework", the row "on paper · photo"): the hub asks the query `homeworkPaperPhotoUrl(assignmentId,
 * fileRef) → {url, expiresAt}` — a read of a file, no read model; the link is good for 10 minutes —
 * and shows the photo at that URL in a modal.
 *
 * Refusals, each with its OWN line (the API's coded, already-localised message, verbatim — portal
 * conventions): HOMEWORK_NOT_YOUR_CLIENT, HOMEWORK_NOT_FOUND, HOMEWORK_PHOTO_UNAVAILABLE. Anything
 * else is the generic "could not be loaded" plus Sentry. An image that fails to LOAD says so too — and
 * which of two things it is depends on the clock (F-220): the link was minted a moment ago, so a failure
 * BEFORE `expiresAt` is the FILE (an iPhone HEIC a browser cannot show) — "can't show this file", with an
 * "Open the file" link and no "Try again"; at or after `expiresAt` the link expired — the expiry line,
 * with "Try again" for a fresh one.
 */
type Props = {
  /** The assignment and the reference to open; null closes the viewer. */
  photo: { assignmentId: string; fileRef: string } | null;
  onClose: () => void;
};

type Minted = { url: string; expiresAt: string };

export const PaperPhotoViewer = ({ photo, onClose }: Props) => {
  const t = useTranslate();
  const [imageFailed, setImageFailed] = useState<'cannotDisplay' | 'expired' | null>(null);
  const { data, isFetching, isError, error, refetch } = useCustom<Minted>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'homeworkPaperPhotoUrl',
      fields: ['url', 'expiresAt'],
      variables: {
        assignmentId: {
          name: 'assignmentId',
          type: 'ID!',
          value: photo?.assignmentId,
        },
        fileRef: { name: 'fileRef', type: 'String!', value: photo?.fileRef },
      },
    },
    // a signed link is a short-lived secret: never served from the cache of an earlier open
    queryOptions: { enabled: Boolean(photo), retry: false, cacheTime: 0 },
  });

  useEffect(() => {
    setImageFailed(null);
  }, [photo?.assignmentId, photo?.fileRef]);

  const info = isError ? graphqlErrorInfo(error) : null;
  useEffect(() => {
    if (isError && !(info?.code && info.message)) Sentry.captureException(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const url = data?.data?.url;

  return (
    <Modal
      open={Boolean(photo)}
      onCancel={onClose}
      title={t('homework.photo.title')}
      width={720}
      destroyOnClose
      footer={
        <Button onClick={onClose} data-testid="homework-photo-close">
          {t('homework.photo.close')}
        </Button>
      }
    >
      <div data-testid="homework-photo-viewer" style={{ textAlign: 'center' }}>
        {isFetching ? (
          <Spin data-testid="homework-photo-loading" />
        ) : isError ? (
          <Alert
            type="error"
            showIcon
            style={{ textAlign: 'start' }}
            message={info?.code && info.message ? info.message : t('homework.photo.loadFailed')}
            data-testid={`homework-photo-refusal-${info?.code ?? 'other'}`}
          />
        ) : url ? (
          <>
            {imageFailed === 'cannotDisplay' ? (
              <Alert
                type="warning"
                showIcon
                style={{ textAlign: 'start', marginBottom: 8 }}
                message={t('homework.photo.cannotDisplay')}
                action={
                  <Button
                    size="small"
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="homework-photo-open-file"
                  >
                    {t('homework.photo.openFile')}
                  </Button>
                }
                data-testid="homework-photo-cannot-display"
              />
            ) : null}
            {imageFailed === 'expired' ? (
              <Alert
                type="warning"
                showIcon
                style={{ textAlign: 'start', marginBottom: 8 }}
                message={t('homework.photo.imageFailed')}
                action={
                  <Button
                    size="small"
                    onClick={() => {
                      setImageFailed(null);
                      refetch();
                    }}
                    data-testid="homework-photo-retry"
                  >
                    {t('homework.photo.retry')}
                  </Button>
                }
                data-testid="homework-photo-image-failed"
              />
            ) : null}
            <img
              src={url}
              alt={t('homework.photo.alt')}
              style={{ maxWidth: '100%', maxHeight: '70vh' }}
              onError={() => setImageFailed(photoFailureOf(data?.data?.expiresAt))}
              data-testid="homework-photo-image"
            />
          </>
        ) : null}
      </div>
    </Modal>
  );
};
